<template>
    <div class="home-app-bar">
        <v-app-bar app elevation="1" height="80">
            <router-link to="/">
                <v-img
                    data-cy="getac-logo"
                    :src="logoUrl"
                    class="getac-nav-logo"
                    @click="
                        clearActive = true
                        isHome = true
                    "
                />
            </router-link>
            <v-spacer />

            <div>
                <v-tabs background-color="transparent" class="hidden-sm-and-down" optional show-arrows>
                    <template v-for="(navItem, i) in navigationItems">
                        <v-tab
                            v-if="!navItem.multiple"
                            :key="i"
                            :href="navItem.override"
                            :target="navItem.override && '_blank'"
                            :exact="false"
                            :ripple="false"
                            class="font-weight-regular nav-font"
                            :style="clearActive ? '' : activeItem(navItem.name)"
                            :class="{ initial: isHome }"
                            min-width="96"
                            :data-cy="navItem.dataCy"
                            hide-slider
                            text
                            @click="goTo(navItem)"
                        >
                            {{ navItem.title }}
                        </v-tab>
                        <v-menu
                            v-else-if="navItem.multiple && navItem.name === 'sm-inventory'"
                            :key="navItem.dataCy"
                            open-on-hover
                            bottom
                            offset-y
                            close-delay="250"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-tab
                                    data-cy="sm-inventory-tab"
                                    :exact="false"
                                    :ripple="false"
                                    class="font-weight-regular nav-font"
                                    min-width="96"
                                    hide-slider
                                    text
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="clearActive = true"
                                >
                                    {{ navItem.title }}
                                </v-tab>
                            </template>

                            <v-list>
                                <v-list-item
                                    v-for="(item, index) in smInventoryDropdown"
                                    :key="index"
                                    align="start"
                                    @click="goToRoute(item.routeName)"
                                >
                                    <v-list-item-title class="nav-font">{{ item.title }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <v-menu
                            v-else-if="navItem.multiple && navItem.name === 'create-rma'"
                            :key="navItem.name"
                            open-on-hover
                            bottom
                            offset-y
                            close-delay="250"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-tab
                                    data-cy="nav-create-rma"
                                    :exact="false"
                                    :ripple="false"
                                    class="font-weight-regular nav-font"
                                    min-width="96"
                                    hide-slider
                                    text
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="clearActive = true"
                                >
                                    {{ navItem.title }}
                                </v-tab>
                            </template>

                            <v-list>
                                <v-list-item
                                    v-for="(item, index) in rmaUpgradeDropdown"
                                    :key="index"
                                    align="start"
                                    @click="goToRoute(item.routeName)"
                                >
                                    <v-list-item-title class="nav-font">{{ item.title }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>

                    <v-menu
                        v-if="$auth.isAuthenticated"
                        open-on-hover
                        bottom
                        offset-y
                        close-delay="250"
                        content-class="account-menu__content"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-tab
                                data-cy="signed-in-tab"
                                :to="{ name: 'your-account' }"
                                :exact="false"
                                :ripple="false"
                                class="font-weight-regular nav-font"
                                min-width="96"
                                hide-slider
                                text
                                v-bind="attrs"
                                v-on="on"
                                @click="clearActive = true"
                            >
                                Your Account
                            </v-tab>
                        </template>

                        <v-list class="account-menu" nav>
                            <div class="account-menu__heading">
                                <v-icon class="account-menu__heading-icon">{{ mdiAccountCircleOutline }}</v-icon>
                                <div>
                                    <span class="account-menu__eyebrow">ACCOUNT CENTER</span>
                                    <strong class="account-menu__heading-title">Your workspace</strong>
                                    <p>Profile, settings &amp; services</p>
                                </div>
                            </div>
                            <v-divider class="account-menu__divider" />
                            <div class="account-menu__group-label">QUICK ACCESS</div>
                            <v-list-item
                                class="account-menu__item account-menu__item--overview"
                                @click="accountOverview"
                            >
                                <v-list-item-icon class="account-menu__icon-wrap">
                                    <v-icon>{{ mdiAccountBoxOutline }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Account Overview</v-list-item-title>
                                    <v-list-item-subtitle
                                        >RMAs, parts orders, quotes, products, and organizations</v-list-item-subtitle
                                    >
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item
                                v-if="hasGssOrgId"
                                class="account-menu__item account-menu__item--dashboard"
                                @click="yourDashboard"
                            >
                                <v-list-item-icon class="account-menu__icon-wrap">
                                    <v-icon>{{ mdiViewDashboardOutline }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Dashboard</v-list-item-title>
                                    <v-list-item-subtitle>Service request data and activity</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item
                                class="account-menu__item account-menu__item--manual"
                                href="https://storage.googleapis.com/gusa-assets/documents/Getac%20Service%20Portal%20Manual.pdf"
                                target="_blank"
                            >
                                <v-list-item-icon class="account-menu__icon-wrap">
                                    <v-icon>{{ mdiBookOpenPageVariantOutline }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Service Portal Manual</v-list-item-title>
                                    <v-list-item-subtitle>Guides and website information</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider class="account-menu__divider" />
                            <div class="account-menu__group-label">ACCOUNT</div>
                            <v-list-item
                                v-for="(item, index) in myAccountDropDown"
                                :key="index"
                                class="account-menu__item"
                                :class="{
                                    'account-menu__item--settings': item.title === 'Account Settings',
                                    'account-menu__item--signout': item.title === 'Sign Out'
                                }"
                                @click="item.method"
                            >
                                <v-list-item-icon class="account-menu__icon-wrap">
                                    <v-icon>{{ item.icon }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-tab
                        v-else
                        data-cy="sign-in-tab"
                        :ripple="false"
                        class="font-weight-regular nav-font"
                        min-width="96"
                        hide-slider
                        text
                        @click="signIn"
                    >
                        Sign In
                    </v-tab>
                </v-tabs>
            </div>

            <v-app-bar-nav-icon class="hidden-md-and-up" @click="drawer = !drawer" />
        </v-app-bar>

        <home-drawer v-model="drawer" :items="drawerItems" :go-to="goTo" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import gql from 'graphql-tag'
import {
    mdiAccountCircleOutline,
    mdiViewDashboardOutline,
    mdiBookOpenPageVariantOutline,
    mdiAccountBoxOutline,
    mdiAccountCogOutline,
    mdiLogoutVariant
} from '@mdi/js'
// import { rolesMap } from '@/config'

export default {
    name: `HomeAppBar`,

    components: {
        HomeDrawer: () => import(`./drawer`)
    },
    apollo: {
        queryOrg: {
            query: gql`
                query QueryOrg {
                    queryOrg {
                        gssOrgId
                        orgId
                        orgName
                    }
                }
            `,
            update(data) {
                if (data && data.queryOrg && data.queryOrg.gssOrgId) {
                    this.hasGssOrgId = true
                }
            }
        },
        querySMCustomer: {
            query: gql`
                query QuerySMCustomer($customerId: String!) {
                    querySMCustomer(customerId: $customerId) {
                        smConfirmed
                    }
                }
            `,
            skip() {
                return !this.$auth.user.org || !this.$auth.user.org.branchId
            },
            variables() {
                return {
                    customerId: this.$auth.user.org.branchId
                }
            }
        }
    },

    data() {
        return {
            drawer: null,
            clearActive: false,
            path: this.$route.name,
            isHome: false,
            hasGssOrgId: false,
            mdiAccountCircleOutline,
            mdiViewDashboardOutline,
            mdiBookOpenPageVariantOutline,
            mdiAccountBoxOutline,
            logoUrl: process.env.VUE_APP_PUBLIC_ASSETS_URL + `/logos/getac-logo.svg`,
            items: [
                {
                    name: `create-rma`,
                    title: `Create RMA`,
                    dataCy: `nav-create-rma`
                },
                {
                    name: `parts-order`,
                    title: `Computer Parts Order`,
                    dataCy: `nav-parts-order`
                },
                {
                    name: `parts-usage`,
                    title: `Parts Usage`,
                    dataCy: `nav-parts-usage`
                },
                {
                    name: `upgrade-device`,
                    title: `Upgrade Device`,
                    dataCy: `nav-parts-usage`
                },
                {
                    name: `help-support`,
                    title: `Support`,
                    dataCy: `nav-help-support`
                }
            ]
        }
    },

    computed: {
        ...mapGetters({
            cart: `cart/cart`
        }),
        isMultiplRMAUser() {
            const userPerms = this.$auth.user && this.$auth.user.roles ? this.$auth.user.roles : []
            const userPermsArray = Array.isArray(userPerms) ? userPerms : [userPerms]
            return userPermsArray.includes('service:rma:multiple')
        },
        //        videoSerialPermission() {
        //            return this.$auth.allRoles(rolesMap.videoSerialPermission)
        //        },
        myAccountDropDown() {
            const dropdowns = []
            //     { title: `Your RMAs`, method: this.yourRmas },
            //     { title: `Your Parts Orders`, method: this.yourPartsOrders },
            //     {
            //         title: `Product Registration`,
            //         method: this.productRegistration
            //     },
            //     {
            //         title: 'Saved Quotes',
            //         method: this.yourQuotes
            //     }
            // ]
            // if (this.$auth.userInOrganization()) {
            //     dropdowns.push({ title: `Organizations`, method: this.organizations })
            // }
            dropdowns.push(
                { title: `Account Settings`, method: this.yourAccount, icon: mdiAccountCogOutline },
                { title: `Sign Out`, method: this.signOut, icon: mdiLogoutVariant }
            )
            return dropdowns
        },
        smInventoryDropdown() {
            const dropdowns = [
                { title: `Product Registration`, routeName: 'product-registration' },
                { title: `Inventory`, routeName: 'sm-inventory' }
                // {
                //     title: `Parts Usage`,
                //     routeName: 'parts-usage'
                // }
            ]
            if (this.querySMCustomer && !this.querySMCustomer.smConfirmed) {
                dropdowns.splice(2, 0, {
                    title: `Inventory Profile`,
                    routeName: 'sm-inventory-profile'
                })
            }
            return dropdowns
        },
        rmaUpgradeDropdown() {
            const dropdowns = [
                { title: `Create RMA`, routeName: 'create-rma' },
                { title: `Upgrade Device`, routeName: 'upgrade-device' }
            ]
            if (this.isMultiplRMAUser) {
                // insert multiple RMA option after Create RMA
                dropdowns.splice(1, 0, {
                    title: `Create Multiple Video RMA`,
                    routeName: 'create-rma-multi-sn'
                })
            }
            return dropdowns
        },
        navigationItems() {
            const navItems = [
                {
                    name: `create-rma`,
                    title: `Create RMA/Upgrade`,
                    dataCy: `nav-create-rma`,
                    multiple: true
                },
                {
                    name: `video-parts-order`,
                    title: `Video Parts Order`,
                    dataCy: `nav-video-parts-order`,
                    multiple: false
                },
                {
                    name: `parts-order`,
                    title: `Computer Parts Order`,
                    dataCy: `nav-parts-order`,
                    multiple: false
                },
                {
                    name: `sm-inventory`,
                    title: `Inventory`,
                    dataCy: `nav-sm-inventory`,
                    multiple: true
                },
                {
                    name: 'drivers',
                    title: `Drivers`,
                    dataCy: `nav-drivers`
                },
                {
                    name: `help-support`,
                    title: `Support`,
                    dataCy: `nav-help-support`,
                    multiple: false
                }
            ]
            return navItems
        },
        drawerItems() {
            if (this.$auth.isAuthenticated)
                return [
                    ...this.navigationItems,
                    { name: `your-account`, title: `Your Account` },
                    { name: `sign-out`, title: `Sign Out` }
                ]
            else return [...this.navigationItems, { name: `sign-in`, title: `Sign In` }]
        }
    },

    created() {
        this.checkCart()
    },

    methods: {
        ...mapActions({
            remove: `cart/remove`
        }),
        checkCart() {
            if (this.cart.length > 0) {
                function addDays(current, days) {
                    const date = new Date(current)
                    date.setDate(date.getDate() + days)
                    return date
                }
                this.cart.forEach((e) => {
                    const date = new Date()
                    const expired = addDays(e.storeDate, 2)
                    if (date > expired) {
                        this.remove({
                            ...e
                        })
                    }
                })
            }
        },
        activeItem(v) {
            const path = this.$route.name
            const current = v
            if (path === current) {
                return 'color: #db5120'
            } else {
                return ''
            }
        },
        goTo(v) {
            this.clearActive = true
            this.isHome = false
            if (v.name === `create-rma` && this.$route.name === `create-rma`) {
                v.query = {
                    refresh: Date.now()
                }
                const answer = window.confirm(`Do you really want to leave? You have unsaved changes..`)
                if (!answer) {
                    return
                }
            }

            if (v.name === 'sign-in') return this.signIn()

            this.$router.push(v)
        },
        signIn() {
            this.$auth.loginWithRedirect()
        },

        signOut() {
            this.$auth.logout()
        },

        accountOverview() {
            this.$router.push({ name: `account-dashboard` })
        },

        yourAccount() {
            this.$router.push({ name: `account-settings` })
        },

        yourDashboard() {
            this.$router.push({ name: `dashboard` })
        },

        productRegistration() {
            this.$router.push({ name: `product-registration` })
        },

        yourRmas() {
            this.$router.push({ name: `rma-history` })
        },

        organizations() {
            this.$router.push({ name: `organizations` })
        },

        yourPartsOrders() {
            this.$router.push({ name: `parts-orders-history` })
        },

        // drivers() {
        //     this.$router.push({ name: `drivers` })
        // },

        yourQuotes() {
            this.$router.push({ name: `quotes` })
        },

        goToRoute(name) {
            this.$router.push({ name: name })
        }

        // savedQuotes() {
        //     this.$router.push({ name: 'your-account/saved-quotes' })
        // }
    }
}
</script>

<style lang="scss">
.account-menu__content {
    margin-top: 10px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.11);
    border-top: 3px solid #db5120;
    border-radius: 13px;
    background: #252830 !important;
    box-shadow: 0 18px 44px rgba(0, 0, 0, 0.42), 0 2px 8px rgba(219, 81, 32, 0.12) !important;
}
.account-menu.v-list {
    min-width: 310px;
    padding: 10px;
    color: #f2f4fa !important;
    background: radial-gradient(ellipse at 0 0, rgba(219, 81, 32, 0.1), transparent 58%), #282b33 !important;
    font-family: 'Work Sans', 'Roboto', sans-serif;
}
.account-menu__heading {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 2px 2px 10px;
    padding: 14px 12px 16px;
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 10px;
    background: linear-gradient(115deg, rgba(219, 81, 32, 0.12), rgba(255, 255, 255, 0.025) 72%);
}
.account-menu__heading-icon.v-icon {
    display: inline-flex;
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 154, 116, 0.35);
    border-radius: 12px;
    color: #ff9a74;
    background: rgba(219, 81, 32, 0.15);
    font-size: 23px;
}
.account-menu__eyebrow {
    display: block;
    margin-bottom: 2px;
    color: #ffad8d;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.5px;
}
.account-menu__heading-title {
    display: block;
    color: #f7f8fb !important;
    font-family: 'Work Sans', 'Roboto', sans-serif;
    font-size: 15px;
    font-weight: 650;
    letter-spacing: -0.25px;
    line-height: 1.25;
}
.account-menu__heading p {
    margin: 2px 0 0;
    color: #c3c9d4 !important;
    font-family: inherit;
    font-size: 10px;
    letter-spacing: 0.05px;
}
.account-menu__group-label {
    padding: 8px 12px 5px;
    color: #aeb7c6 !important;
    font-family: 'Work Sans', 'Roboto', sans-serif;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.25px;
}
.account-menu__divider.v-divider {
    margin: 8px 9px 3px;
    border-color: rgba(255, 255, 255, 0.1);
}
.account-menu .account-menu__item {
    position: relative;
    min-height: 52px;
    margin: 2px 0;
    padding: 0 12px;
    border: 1px solid transparent;
    border-radius: 9px;
    color: #f0f2f7 !important;
    font-family: 'Work Sans', 'Roboto', sans-serif;
    text-decoration: none;
    transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease, transform 150ms ease;
}
.account-menu .account-menu__item:hover,
.account-menu .account-menu__item:focus {
    color: #fff !important;
    border-color: rgba(240, 120, 76, 0.17);
    background: linear-gradient(100deg, rgba(219, 81, 32, 0.19), rgba(219, 81, 32, 0.06));
    transform: translateX(2px);
}
.account-menu .account-menu__item .v-list-item__icon {
    margin: 0 12px 0 0;
    align-self: center;
}
.account-menu .account-menu__item .account-menu__icon-wrap {
    display: inline-flex;
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 154, 116, 0.16);
    border-radius: 9px;
    background: rgba(219, 81, 32, 0.09);
}
.account-menu .account-menu__item .account-menu__icon-wrap .v-icon {
    color: #ff9a74;
    font-size: 18px;
}
.account-menu .account-menu__item .v-list-item__title {
    color: #f3f5f8 !important;
    font-family: 'Work Sans', 'Roboto', sans-serif;
    font-size: 12px;
    font-weight: 650;
    letter-spacing: 0.08px;
    line-height: 1.35;
}
.account-menu .account-menu__item .v-list-item__subtitle {
    margin-top: 3px;
    color: #c0c7d3 !important;
    font-family: 'Work Sans', 'Roboto', sans-serif;
    font-size: 10px;
    font-weight: 400;
    letter-spacing: 0.08px;
    line-height: 1.35;
}
.account-menu .account-menu__item--signout {
    min-height: 46px;
    color: #f2c3ba !important;
}
.account-menu .account-menu__item--signout .v-list-item__title {
    color: #f2c3ba !important;
}
.account-menu .account-menu__item--signout .account-menu__icon-wrap {
    border-color: rgba(226, 140, 125, 0.18);
    background: rgba(190, 70, 55, 0.1);
}
.account-menu .account-menu__item--signout .v-icon {
    color: #e79a8d;
}
.account-menu .account-menu__item--signout:hover,
.account-menu .account-menu__item--signout:focus {
    border-color: rgba(226, 140, 125, 0.2);
    background: linear-gradient(100deg, rgba(190, 70, 55, 0.2), rgba(190, 70, 55, 0.07));
}
.account-menu__content:focus-visible,
.account-menu .account-menu__item:focus-visible {
    outline: 2px solid #ff9a74;
    outline-offset: 2px;
}
@media (max-width: 600px) {
    .account-menu__content {
        max-width: calc(100vw - 20px);
    }
    .account-menu.v-list {
        min-width: 280px;
    }
}
.home-app-bar {
    .v-tabs-slider {
        margin: 0 auto;
        max-width: 0;
    }

    .getac-nav-logo {
        max-width: 128px;
        width: 100%;
    }
}
.initial {
    color: rgba(0, 0, 0, 0.54) !important;
}
</style>
